import React, { useEffect, useMemo, useState, useRef } from 'react'
import { RouteComponentProps, useParams } from 'react-router-dom'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { FormikProps } from 'formik'

import LoginTemplate, { LoginTemplateProps } from '../../templates/Login'
import { useHeaderProps } from '../../components/Header/useProps'
import Header from '../../components/Header'
import { actions, selectors } from '../../redux'
import i18n from '../../i18n'
import { LoginFormValues } from '../../components/forms/LoginForm'

const LoginPage: React.FC<RouteComponentProps> = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const routerParams = useParams<{ locale: string }>()

  // SELECTORS

  const loginService = useSelector(selectors.auth.login)
  const forgetPasswordService = useSelector(selectors.auth.forgetPassword)

  // REF

  const $formikRef = useRef<FormikProps<LoginFormValues>>(null)

  // STATE

  const [languageSet, setLanguageSet] = useState(false)
  const [isForgotPassword, setIsForgotPassword] = useState(false)

  // PROPS

  const headerProps = useHeaderProps()
  const templateProps: LoginTemplateProps = useMemo(
    () => ({
      layoutProps: {
        header: <Header {...headerProps} />,
      },
      title: !isForgotPassword ? t('login.title') : undefined,
      image: !isForgotPassword
        ? {
            src: '/static/images/login.png',
          }
        : undefined,
      loginForm: !isForgotPassword
        ? {
            formik: {
              innerRef: $formikRef,
              validationSchema: Yup.object().shape({
                email: Yup.string().required().email(),
                password: Yup.string().required(),
              }),
              initialValues: {
                email: '',
                password: '',
              },
              onSubmit: (values) => {
                dispatch(
                  actions.auth.loginRequest({
                    email: values.email,
                    password: values.password,
                  })
                )
              },
            },
            // text: t('login.text'),
            fields: {
              email: {
                name: 'email',
                label: t('login.form.email.label'),
                placeholder: t('login.form.email.placeholder'),
                required: true,
              },
              password: {
                name: 'password',
                label: t('login.form.password.label'),
                placeholder: t('login.form.password.placeholder'),
                required: true,
              },
            },
            forgotPassword: {
              text: t('login.forgotPassword'),
              onClick: () => {
                setIsForgotPassword((prevState) => !prevState)
              },
            },
            submitErrors: loginService.errors?.length ? [{ label: t('login.error') }] : undefined,
            submitButton: {
              isPending: loginService.pending,
              isSuccess: loginService.success,
              text: t('login.submitButton'),
            },
          }
        : undefined,
      forgotPassword: isForgotPassword
        ? {
            form: {
              formik: {
                validationSchema: Yup.object().shape({
                  email: Yup.string().required().email(),
                }),
                initialValues: {
                  email: '',
                },
                onSubmit: (values) => {
                  dispatch(
                    actions.auth.forgetPasswordRequest({
                      email: values.email,
                    })
                  )
                },
              },
              fields: {
                email: {
                  name: 'email',
                  label: t('forgotPassword.form.email.label'),
                  placeholder: t('forgotPassword.form.email.placeholder'),
                  required: true,
                },
              },
              title: t('forgotPassword.title'),
              desc: t('forgotPassword.text'),
              successMessage: forgetPasswordService.success
                ? t('forgotPassword.successMessage')
                : '',
              submitButton: {
                isPending: forgetPasswordService.pending,
                isSuccess: forgetPasswordService.success,
                text: t('forgotPassword.submitButton'),
              },
            },
            back: {
              label: t('forgotPassword.back'),
              link: {
                onClick: () => {
                  setIsForgotPassword(false)
                  dispatch(actions.auth.forgetPasswordReset())
                },
              },
            },
          }
        : undefined,
    }),
    [
      headerProps,
      t,
      isForgotPassword,
      loginService.errors?.length,
      loginService.pending,
      loginService.success,
      forgetPasswordService.pending,
      forgetPasswordService.success,
      dispatch,
    ]
  )

  // EFFECT

  useEffect(() => {
    i18n.changeLanguage(routerParams.locale).then(() => {
      setLanguageSet(true)
    })
  }, [routerParams.locale])

  // RETURN

  return <>{languageSet && <LoginTemplate {...templateProps} />}</>
}

export default LoginPage
